export interface PaginationParams {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface AllPlansResponse {
  plans: Plan[];
  filterOptions: FilterOptions;
}

export const HIERARCHICAL_MODELS_TABLE = 'HierarchicalModels';

export interface HierarchicalModel {
  HierarchicalModelId: number;
  Designation: string;
  ReportsTo: number;
  BossId: number;
}

export interface TimePeriodData {
  APTimePeriodId: number;
  Name: string;
  StartDate: string;
  EndDate: string;
  FrequencyId: number;
  RiskMatrixScopeId: number;
}

export interface PaginatedPlansResponse extends PaginatedResponse<Plan> {
  filterOptions: FilterOptions;
  periods: TimePeriodData[];
  scopeName: string;
  enterpriseName: string;
}

export const AP_TABLE = 'APs';

export interface Plan {
  APId: number;
  HierarchicalModelId: number;
  HierarchicalModelName: string;
  RiskMatrixScopeId: number;
  Status: string;
  Year: number;
  Created_at: string;
  ResponsibleUser: string;
  BusinessUnit?: string;
  APVersionId?: number;
  APVersionState?: string;

  APFulfillmentsVersionId?: number;
  APFulfillmentsVersionState?: string;
  APVersionsCount?: number;
  APFulfillmentsVersionCount?: number;
}

export const AP_OBJECTIVES_TABLE = 'APObjectives';

export interface Objective {
  APObjectiveId: number;
  APFocusId: number;
  APVersionId: number;
  Name: string;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
  ActivityCount?: number;
}

export interface CreateObjectiveInput {
  APFocusId: number;
  APVersionId: number;
  Name: string;
}

export interface UpdateObjectiveInput {
  Name?: string;
}

export interface ObjectiveBatchSaveInput {
  create: Array<{
    APFocusId: number;
    Name: string;
  }>;
  update: Array<{
    APObjectiveId: number;
    Name: string;
  }>;
  delete: number[];
}

export interface ObjectiveBatchSaveResult {
  created: Objective[];
  updated: Objective[];
  deleted: number[];
  objectives: Objective[];
}

export interface CreatePlanInput {
  HierarchicalModelId: number;
  RiskMatrixScopeId: number;
  Year: number;
  Status?: (typeof PLAN_STAGES)[keyof typeof PLAN_STAGES];
  OutOfTimeRequestId?: number | null;
}

export const USERS_TABLE = 'Users';

export interface User {
  UserId: number;
  Name: string;
  UserName: string;
  Email: string;
  Active: boolean;
  Level: number;
  Language: string;
  HierarchyId: number;
}

export interface FilterOptions {
  hierarchicalModels: HierarchicalModel[];
  status: string[];
  years: string[];
}

export interface AnnualPlanExportFilters {
  hierarchicalModelId?: string;
  status?: string;
  year?: string;
  fromDate?: string;
  toDate?: string;
}

export const AP_FOCUSES_TABLE = 'APFocus';
export const AP_FOCUS_YEARS_TABLE = 'APFocusYears';

export interface Focus {
  APFocusId: number;
  Name: string;
  NameOriginal?: string | null;
  RiskMatrixScopeId: number;
  Year: number;
  Description: string;
  UserId: number;
  Weight: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;

  TotalRequired?: number;
  SatisfiedRequired?: number;
  NotSatisfiedRequired?: number;
  AdherencePct?: number;
  StartDate?: string;
  EndDate?: string;
}

export const AP_REQUIREMENTS_TABLE = 'APRequirements';
export const AP_REQUIREMENTS_YEARS_TABLE = 'APRequirementsYears';

export interface Requirement {
  APRequirementId: number;
  APRequirementTypeId: number;
  Year: number;
  Name: string;
  Description: string;
  UserId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export const AP_REQUIREMENTS_TYPES_TABLE = 'APRequirementTypes';

export interface RequirementType {
  APRequirementTypeId: number;
  Name: string;
  isRequired: boolean;
  Description: string;
  UserId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export const ANNUAL_PLAN_GROUP_ACTIVITIES_TABLE = 'APGroupActivities';

export interface Activity {
  APGroupActivityId: number;
  APObjectiveId: number;
  APFocusId?: number | null;
  APRequirementId: number;
  UserId: number;
  GroupActivityPhotos: string;
  GroupActivityName: string;
  StartDate: string;
  EndDate: string;
  GroupActivityType: string;
  Description: string;
  ApplyRequirement: boolean;
  CompletionProgress?: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export interface CreateActivityInput {
  APObjectiveId: number;
  APRequirementId: number;
  GroupActivityName: string;
  StartDate: string;
  EndDate: string;
  GroupActivityType: string;
  Description: string;
  ApplyRequirement: boolean;
}

export interface ActivityBatchSaveInput {
  create: CreateActivityInput[];
  update: Array<Partial<CreateActivityInput> & { APGroupActivityId: number }>;
  delete: number[];
}

export interface ActivityBatchSaveResult {
  created: Activity[];
  updated: Activity[];
  deleted: number[];
  activities: Activity[];
}

export const AP_FOCUS_REQUIREMENTS_TABLE = 'APFocusRequirements';
export const AP_FOCUS_REQUIREMENTS_YEARS_TABLE = 'APFocusRequirementsYears';

export interface FocusRequirement {
  APFocusRequirementId: number;
  APRequirementId: number;
  APFocusId: number;
  Year: number;
  UserId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export const AP_FOCUS_CHECKLISTS_TABLE = 'APChecklists';
export const AP_CHECKLISTS_YEARS_TABLE = 'APChecklistsYears';

export interface APChecklists {
  APChecklistId: number;
  RiskMatrixScopeId: number;
  Year: number;
  UserId: number;
  ChecklistId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export interface Checklist {
  ChecklistId: number;
  Name: string;
  CreatedAt: string;
  isDeleted: boolean;
  Created_at: string;
  Updated_at: string;
  Sort: number;
  ContractorId: number;
  CheckListTypeId: number;
  LabelDescriptionField: string;
  IsSequence: boolean;
  IsRegularlyUsed: boolean;
}

export const CHECKLIST_QUESTIONS_TABLE = 'ChecklistQuestions';

export interface ChecklistQuestion {
  ChecklistQuestionId: number;
  ChecklistId: number;
  Type: string;
  Question: string;
  WithPhoto: boolean;
  WithDescription: boolean;
  CreatedAt: string;
  isDeleted: boolean;
  Created_at: string;
  Updated_at: string;
  QuestionWeight: number;
  Sort: number;
  QuestionWeightFloat: number;
  WithFile: boolean;
}

export const CHECKLIST_TYPES_TABLE = 'CheckListTypes';

export interface ChecklistType {
  CheckListTypeId: number;
  Name: string;
  Created_at: string;
  Updated_at: string;
  IsDeleted: boolean;
}

export const CHECKLIST_OPTIONS_TABLE = 'ChecklistOptions';

export interface ChecklistOption {
  ChecklistOptionId: number;
  ChecklistQuestionId: number;
  Name: string;
  OptionWeight: number;
  isDeleted: boolean;
  Created_at: string;
  Updated_at: string;
  Sort: number;
  isAlertCreate: boolean;
  OptionWeightFloat: number;
  DefaultOption: boolean;
  isActivityCreate: boolean;
  NextCheckListQuestionId: number | null;
  isBehaviorobservationCreate: boolean;
}

export const AP_CHECKLIST_RESPONSES_TABLE = 'APChecklistResponses';

export interface ChecklistResponse {
  APChecklistResponseId: number;
  APFulfillmentsVersionId: number;
  UserId: number;
  ChecklistId: number;
  APChecklistId: number;
  Comment: string | null;
  IsFinished: boolean;
  TotalRatingFloat: number | null;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
}

export const AP_CHECKLIST_ANSWERS_TABLE = 'APChecklistAnswers';

export interface ChecklistAnswer {
  APChecklistAnswerId: number;
  APChecklistResponseId: number;
  ChecklistQuestionId: number;
  ChecklistOptionId: number | null;
  AnswerComment: string | null;
  AnswerPhoto: string | null;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
}

export const AP_CHECKLIST_CUSTOM_FIELD_VALUES_TABLE = 'APChecklistCustomFieldValues';

export interface ChecklistCustomFieldValue {
  APChecklistCustomFieldValueId: number;
  APChecklistResponseId: number;
  CustomFieldId: number;
  NumericValue: number;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
}

export const AP_PERIOD_TYPES_TABLE = 'APPeriodTypes';

export interface PeriodType {
  APPeriodTypeId: number;
  Name: string;
  UserId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export const AP_TIME_PERIODS_TABLE = 'APTimePeriods';

export interface APTimePeriod {
  APTimePeriodId: number;
  RiskMatrixScopeId: number;
  APPeriodTypeId: number;
  TimePeriodId: number;
  UserId: number;
  IsDeleted: boolean;
  Created_at: string;
  Updated_at: string;
}

export const AP_FOCUS_DEFINITION_APPROVALS_TABLE = 'APFocusDefinitionApprovals';

export interface FocusDefinitionApproval {
  APFocusDefinitionApprovalId: number;
  APVersionId: number;
  APFocusId: number;
  Approved: boolean;
  UserId: number;
  Created_at: string;
  Updated_at: string;
  IsDeleted: boolean;
}

export interface CreateFocusDefinitionApprovalInput {
  APFocusId: number;
  Approved: boolean;
}

export const AP_DEFINITION_APPROVALS_TABLE = 'APDefinitionApprovals';

export interface PlanDefinitionApproval {
  APDefinitionApprovalId: number;
  APVersionId: number;
  Approved: boolean;
  CEOConfirmation: boolean;
  Comments: string;
  UserId: number;
  Created_at: string;
  Updated_at: string;
  IsDeleted: boolean;
}

export interface CreatePlanDefinitionApprovalInput {
  Approved: boolean;
  CEOConfirmation: boolean;
  Comments: string;
  IsDraft: boolean;
}

export interface FocusRequirementWithDetails {
  APFocusId: number;
  RequirementTypes: {
    requirementType: RequirementType;
    requirements: Requirement[];
  }[];
}

export interface FocusName {
  APFocusId: number;
  Name: string;
}

export const AP_PLAN_REQUIREMENT_DECISIONS_TABLE = 'APPlanRequirementDecisions';

export interface PlanRequirementDecision {
  APPlanRequirementDecisionId: number;
  APId: number;
  APFocusId: number;
  APRequirementId: number;
  Enabled: boolean;
  Comment: string | null;
  EvidenceName: string | null;
  EvidenceUrl: string | null;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
}

export interface PlanRequirementDecisionInput {
  APId: number;
  APFocusId: number;
  APRequirementId: number;
  Enabled: boolean;
  Comment?: string | null;
  EvidenceName?: string | null;
}

export interface Step0LinkedRequirementActivity {
  id: number;
  objectiveId: number;
  name: string;
}

export interface Step0RequirementActivityLink {
  focusId: number;
  requirementId: number;
  count: number;
  activities: Step0LinkedRequirementActivity[];
}

export interface AnnualPlanStep0Context {
  requirements: FocusRequirementWithDetails[];
  focusNames: FocusName[];
  planRequirementDecisions: PlanRequirementDecision[];
  requirementActivityLinks: Step0RequirementActivityLink[];
}

export interface AnnualPlanStep1Context {
  focuses: Focus[];
  objectives: Objective[];
}

export interface AnnualPlanStep2Context {
  focuses: Focus[];
  objectives: Objective[];
  activities: Activity[];
  requirements: FocusRequirementWithDetails[];
  planRequirementDecisions: PlanRequirementDecision[];
}

export interface ChecklistAnswerBatchInput {
  questionId: number;
  optionId: number | null;
  comment?: string | null;
  photo?: string | null;
}

export interface ChecklistCustomFieldBatchInput {
  customFieldId: number;
  value: number;
}

export interface ChecklistResponseBatchInput {
  APFulfillmentsVersionId: number;
  checklistId: number;
  APChecklistId?: number | null;
  comment?: string | null;
  isFinished: boolean;
  answers: ChecklistAnswerBatchInput[];
  customFields: ChecklistCustomFieldBatchInput[];
}

export interface ChecklistResponsesBatchSaveInput {
  responses: ChecklistResponseBatchInput[];
}

export interface ChecklistResponsesBatchSaveResult {
  saved: Array<{
    APChecklistResponseId: number;
    ChecklistId: number;
    TotalRatingFloat: number | null;
  }>;
}

export interface AnnualPlanComplianceContext {
  versionId: number;
  fulfillmentVersionId: number;
  focuses: Focus[];
  objectives: Objective[];
  activities: Activity[];
  requirements: FocusRequirementWithDetails[];
  checklists: unknown[];
  checklistResponses: unknown[];
  focusProgress: Array<Focus & { FulfillmentProgressPct: number }>;
  globalFulfillmentProgressPct: number;
}

export const PLAN_STAGES = {
  DEFINITION: 'definition',
  DEFINITION_OUT_OF_TIME: 'definition_out_of_time',
  SELF_ASSESSMENT: 'self_assessment',
};

export const PERIOD_TYPES = {
  DEFINITION: 'Definición',
  DEFINITION_MODIFICATION: 'Periodo Modificación Definiciones',
  SELF_ASSESSMENT: 'Autoevaluación',
  SELF_ASSESSMENT_MODIFICATION: 'Periodo Modificación Autoevaluaciones',
};

export const PLAN_VERSION_STATES = {
  IN_PROCESS: 'InProcess',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  AWAITING_APPROVAL: 'AwaitingApproval',
};

export const PLAN_FULFILLMENT_STATES = {
  IN_PROCESS: 'InProcess',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  AWAITING_APPROVAL: 'AwaitingApproval',
};

export const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

export const DEFAULT_ITEMS_PER_PAGE = 10;

export const DEFAULT_PAGE = 1;

export const DEFAULT_SORT_BY = 'Created_at';

export const DEFAULT_FILTERS = {
  hierarchicalModelId: undefined,
  status: undefined,
  year: undefined,
};

export const MAX_FOCUS_NAME_LENGTH = 100;
export const MAX_OBJECTIVE_NAME_LENGTH = 150;
export const MAX_ACTIVITY_NAME_LENGTH = 150;
export const MAX_ACTIVITY_DESCRIPTION_LENGTH = 500;
export const MAX_APPROVAL_COMMENTS_LENGTH = 500;

export const MIN_YEAR = 2000;
export const MAX_YEAR = 2100;

export const CURRENT_YEAR = new Date().getFullYear();

export const YEAR_OPTIONS = Array.from(
  { length: MAX_YEAR - MIN_YEAR + 1 },
  (_, i) => MIN_YEAR + i,
).reverse();

export const MAX_FOCUS_WEIGHT = 100;

export const AP_PERMISSIONS = {
  SELF_ASSESSMENT: 'AP_SELF_ASSESSMENT',
  CEO: 'AP_CEO',
  ADMIN: 'AP_ADMIN',
} as const;

export const AP_OUT_OF_TIME_PLAN_REQUESTS_TABLE = 'APOutOfTimePlanRequests';
export const AP_MATURITY_LEVEL_OVERRIDES_TABLE = 'APMaturityLevelOverrides';
export const AP_AUDIT_RESULTS_TABLE = 'APAuditResults';
export const AP_ASSESSMENT_LEGAL_TABLE = 'APAssessmentLegal';
export const AP_INTEGRATED_EVALUATION_OVERRIDES_TABLE = 'APIntegratedEvaluationOverrides';
export const AP_ENVIRONMENT_PLAN_OVERRIDES_TABLE = 'APEnvironmentPlanOverrides';
export const AP_BI_REPORTS_TABLE = 'APBIReports';

export type OutOfTimePlanRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface OutOfTimePlanRequest {
  APOutOfTimePlanRequestId: number;
  RequesterUserId: number;
  ApproverUserId: number | null;
  Reason: string;
  Status: OutOfTimePlanRequestStatus;
  Created_at: string;
  Updated_at: string;
  ApprovedAt: string | null;
  UsedAt: string | null;
  IsDeleted: boolean;
}

export interface MaturityLevelOverride {
  APMaturityLevelOverrideId: number;
  HierarchicalModelId: number;
  RiskMatrixScopeId: number;
  MaturityLevel: number;
  Year: number;
  Created_at?: string;
  Updated_at?: string;
  IsDeleted?: boolean;
}

export interface AuditResult {
  APAuditResultId: number;
  HierarchicalModelId: number;
  RiskMatrixScopeId: number;
  Percentage: number;
  Year: number;
  Created_at?: string;
  Updated_at?: string;
  IsDeleted?: boolean;
}

export interface IntegratedEvaluationOverride {
  APIntegratedEvaluationOverrideId: number;
  HierarchicalModelId: number;
  RiskMatrixScopeId: number;
  IntegratedEvaluationResult: number;
  Year: number;
  Created_at?: string;
  Updated_at?: string;
  IsDeleted?: boolean;
}

export interface EnvironmentPlanOverride {
  APEnvironmentPlanOverrideId: number;
  HierarchicalModelId: number;
  RiskMatrixScopeId: number;
  WorkPlanResult: number;
  LeadershipPlanResult: number;
  Year: number;
  Created_at?: string;
  Updated_at?: string;
  IsDeleted?: boolean;
}

export const RequirementTypes = {
  MINIMUM: 'Requisitos Mínimos',
  ADVANCED: 'Requisitos Avanzados',
};

export const ActivityTypes = {
  STRATEGIC: 'Estratégica',
  PERMANENT: 'Permanente',
};

export const SCOPE_KEYS = {
  SAFETY: 'SAFETY',
  ENVIRONMENT: 'ENVIRONMENT',
  MAINTENANCE: 'MAINTENANCE',
} as const;

export interface AppSettings {
  annualPlanning: {
    disableViewButtonInEnvironmentScope: boolean;
  };
}

export interface AppSettingsResponse {
  settings: AppSettings;
  scopeConstant: string | null;
}

export interface ExternalHierarchicalModel {
  id: number;
  designation: string;
  reportsTo: number | null;
  type: string;
  bossId: number | null;
  description: string;
  IsDeleted: boolean;
  fromAPI: boolean;
}

export interface ExternalRiskMatrixScope {
  id: number;
  name: string;
  IsDeleted: boolean;
  updatedAt: string;
  isRiskMatrixSeveritiesLegend: boolean;
  constant: string | null;
}

export interface ExternalTimePeriod {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  frequencyId: number;
  enabled: boolean;
  IsDeleted: boolean;
}

export interface ExternalUser {
  id: number;
  name: string;
  email: string;
  userName: string;
  active: boolean;
  level: string;
}

export interface ExternalDataDTO {
  hierarchicalModels: ExternalHierarchicalModel[];
}

export const AP_VALIDATIONS_TABLE = 'APValidations';

export interface PlanValidation {
  APValidationId: number;
  APFulfillmentsVersionId: number;
  Approved: boolean | null;
  CEOConfirmation: boolean;
  Comment: string | null;
  Signature: string | null;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
  IsDraft?: boolean;
}

export interface CreatePlanValidationInput {
  Approved: boolean;
  CEOConfirmation: boolean;
  Comment: string;
  Signature: string;
  IsDraft?: boolean;
}

export const AP_FOCUS_FULFILLMENTS_TABLE = 'APFocusFulfillments';

export interface FocusFulfillment {
  APFocusFulfillmentId: number;
  APFocusId: number;
  APFulfillmentsVersionId: number;
  Approved: boolean | null;
  UserId: number;
  Created_at: string;
  Updated_at: string | null;
  IsDeleted: boolean;
}

export interface CreateFocusFulfillmentInput {
  APFocusId: number;
  Approved: boolean;
}

export interface SelfAssessmentSummaryCard {
  key: 'workPlan' | 'managementFocus' | 'leadershipPlan' | 'operationalImpact';
  value: number | null;
}

export interface SelfAssessmentDiscountReason {
  label: string;
  value: number;
}

export interface SelfAssessmentDiscountEntry {
  focusName: string;
  value: number;
  reasons: SelfAssessmentDiscountReason[];
}

export interface SelfAssessmentFocusDiscountInfo {
  value: number;
  reasons: SelfAssessmentDiscountReason[];
}

export interface SelfAssessmentFocusSummary {
  id: number;
  name: string;
  fulfillmentPct: number | null;
  fulfillmentPctOriginal?: number | null;
  discount?: SelfAssessmentFocusDiscountInfo | null;
  weightingSource?: 'assessmentLegal' | 'audit' | null;
  weight: number | null;
}

export interface SelfAssessmentFocusAdherence {
  id: number;
  name: string;
  adherencePct: number | null;
  notSatisfied: number;
  totalRequired: number;
  satisfiedRequired: number;
  weightedContribution: number | null;
  weight: number;
}

export interface SelfAssessmentChecklistSummary {
  checklistId: number;
  name: string;
  nameOriginal?: string | null;
  progress: number;
}

export interface SelfAssessmentChecklistCategory {
  categoryKey: string;
  categoryName: string;
  categoryNameOriginal?: string | null;
  minScore: number;
  maxScore: number;
  avgScore: number;
  checklists: SelfAssessmentChecklistSummary[];
}

export interface SelfAssessmentLeadershipActivityDetail {
  key: string;
  committed: number;
  performed: number;
  percentage: number | null;
}

export interface SelfAssessmentLeadershipActivities {
  averagePct: number | null;
  details: SelfAssessmentLeadershipActivityDetail[];
  totals: {
    committed: number;
    performed: number;
    percentage: number | null;
  };
}

export interface SelfAssessmentMaturityInfo {
  levelLabel: string;
  levels: Array<{ level: number; percentage: number }>;
}

export interface SelfAssessmentContributionInfo {
  businessLine: string;
  uen: string;
  businessLineIndex: number;
  globalIndex: number;
  inTop80BL: boolean;
  inTop80Global: boolean;
  adjustedTotal: number;
  discountGlobal: number;
  discountBusiness: number;
  uenIfctp: number;
  businessLineIfctp: number;
}

export interface SelfAssessmentResultsPayload {
  selfAssessmentId: number;
  year: number | null;
  scopeName: string;
  planName: string | null;
  businessUnit: string | null;
  isSecurityScope: boolean;
  isSocioAmbientalScope: boolean;
  ytdCutoff: { monthLabel: string; year: number };
  summary: {
    overallAverage: number | null;
    overallWithoutAdjustments?: number | null | undefined;
    cards: SelfAssessmentSummaryCard[];
    discountEntries?: SelfAssessmentDiscountEntry[] | undefined;
    contribution?: SelfAssessmentContributionInfo | null;
  };
  workPlan: {
    globalAdherence: number | null;
    focuses: SelfAssessmentFocusSummary[];
  };
  focusAdherence: {
    totalWeightedAdherence: number | null;
    items: SelfAssessmentFocusAdherence[];
  };
  leadershipPlanResult: number | null;
  managementFocusResult: number | null;
  managementFocusOriginal?: number | null;
  managementFocusAdjusted?: number | null;
  auditPercentage?: number | null;
  operationalImpactResult: number | null;
  maturity: SelfAssessmentMaturityInfo;
  previousMaturityLevel?: number | null;
  previousMaturitySource?: 'override' | 'historical' | 'assumed' | undefined;
  cultureLevel?: number | null;
  leadershipActivities?: SelfAssessmentLeadershipActivities | null;
  checklistCategories: SelfAssessmentChecklistCategory[];
}

export interface PowerBiReportImageResponse {
  dataUrl: string | null;
  node0DataUrl: string | null;
}

export const SELF_ASSESSMENT_RESULTS_CONSTANTS = {
  UNSATISFIED_REQUIREMENT_DISCOUNT_PCT: 10,
  MATURITY_RULES: {
    security: [
      { level: 4, requirements: { 1: 100, 2: 100, 3: 90, 4: 80 } },
      { level: 3, requirements: { 1: 100, 2: 90, 3: 80 } },
      { level: 2, requirements: { 1: 90, 2: 80 } },
    ],
    environment: [
      { level: 4, requirements: { 1: 100, 2: 100, 3: 90, 4: 80 } },
      { level: 3, requirements: { 1: 100, 2: 90, 3: 80 } },
      { level: 2, requirements: { 1: 100, 2: 80 } },
    ],
  },
  MATURITY_CATEGORY_KEYWORD: 'nivel de madurez',
  OPERATIONAL_IMPACT_CATEGORY_KEYWORD: 'impacto en la operativa',
  PERFORMANCE_CHECKLIST_KEYWORDS: ['performance plan de trabajo'],
  QUALITY_PLAN_KEYWORDS: ['calidad del plan de trabajo - seg'],
  LEADERSHIP_PLAN_KEYWORDS: {
    security: ['plan de liderazgo - seg', 'plan de liderazgo seg', 'plan de liderazgo seguridad'],
    environment: [
      'plan de liderazgo ma',
      'plan de liderazgo - ma',
      'plan de liderazgo socioambiental',
    ],
  },
  LEADERSHIP_ACTIVITY_CHECKLIST_KEYWORDS: {
    security: [
      'actividades por cargo - seguridad',
      'actividades por rol',
      'actividades de liderazgo',
    ],
    environment: ['actividades por cargo - ma', 'actividades por cargo - socioambiental'],
  },
  LEADERSHIP_ACTIVITY_PAIRS: [
    {
      key: 'leadershipCeoBu',
      committedName: 'cantidad anual de actividades de liderazgo comprometidas ceo o bu manager',
      performedName: 'cantidad anual de actividades realizadas ceo o bu manager',
    },
    {
      key: 'leadershipManager',
      committedName:
        'cantidad anual de actividades de liderazgo comprometidas manager o submanager',
      performedName: 'cantidad anual de actividades realizadas manager o submanager',
    },
    {
      key: 'leadershipHead',
      committedName: 'cantidad anual de actividades de liderazgo comprometidas head',
      performedName: 'cantidad anual de actividades realizadas head',
    },
    {
      key: 'leadershipSupervisors',
      committedName: 'cantidad anual de actividades de liderazgo comprometidas supervisores',
      performedName: 'cantidad anual de actividades realizadas supervisores',
    },
  ],
  FOCUS_NAME_KEYWORDS: {
    focus1: ['confianza', 'grupos de interés'],
    focus2: ['crisis'],
    focus3: ['housekeeping', 'permiso', 'normativo'],
    focus4: ['estándar', 'cultura'],
  },
  ENVIRONMENT_FOCUS_RESULT_WEIGHTS: {
    focusWithDiscounts: 0.2,
    externalResult: 0.8,
  },
  LEGAL_REQUIREMENT_STATUS: {
    NON_COMPLIANT_TAG_ID: 3,
    IDENTIFIED_NON_COMPLIANCE_ID: 1,
    IN_PROGRESS_NON_COMPLIANCE_ID: 2,
  },
  FOCUS_QUESTION_KEYWORDS: [
    { includes: ['alto potencial'], focusKey: 'focus4' },
    { includes: ['bajo potencial'], focusKey: 'focus4' },
    { includes: ['participado en la investigación'], focusKey: 'focus4' },
    { includes: ['procesos sancionatorios'], focusKey: 'focus3' },
    { includes: ['multas en curso'], focusKey: 'focus3' },
    { includes: ['incumplimientos legales'], focusKey: 'focus3' },
    { includes: ['permisos pendientes'], focusKey: 'focus3' },
    { includes: ['grupos de interés'], focusKey: 'focus1' },
    { includes: ['alto potencial sin gestión'], focusKey: 'focus4' },
    { includes: ['crisis sin gestión'], focusKey: 'focus2' },
    { includes: ['conflictos o crisis'], focusKey: 'focus2' },
    { includes: ['investigaciones de incidentes'], focusKey: 'focus4' },
  ],
  SCOPE_SECURITY_KEYWORDS: ['seguridad', 'security'],
  SCOPE_SOCIO_ENV_KEYWORDS: ['socio', 'ambient'],
  ACCIDENT_ANALYSIS_GLOBAL_FACTOR: 1.5,
  ACCIDENT_ANALYSIS_GLOBAL_FACTOR_NON_TOP80: 1,
  ACCIDENT_ANALYSIS_IFCTP_DISCOUNT_PCT: 10,
  ACCIDENT_ANALYSIS_IFCTP_SCALE: 1_000_000,
} as const;
